// src/pages/ShopPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { getProducts } from '../firebase/services';
import { useAuth } from '../context/AuthContext';

const FOOD_CATS = ['All', 'Italian', 'Japanese', 'Burgers', 'Seafood', 'Desserts', 'Healthy', 'Soups'];
const SHOP_CATS = ['All', 'Fashion', 'Jewelry', 'Accessories', 'Home'];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { userData } = useAuth();

  const type = searchParams.get('type') || '';
  const category = searchParams.get('category') || 'All';
  const sort = searchParams.get('sort') || '';
  const search = searchParams.get('search') || '';
  const featured = searchParams.get('featured') === 'true';
  const maxPrice = Number(searchParams.get('maxPrice') || 5000);
  const minRating = Number(searchParams.get('minRating') || 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProducts({
        type: type || undefined,
        category: category !== 'All' ? category : undefined,
        sort: sort || undefined,
        search: search || undefined,
        featured: featured || undefined,
        maxPrice,
        minRating: minRating || undefined,
      });
      setProducts(data);
    } finally { setLoading(false); }
  }, [type, category, sort, search, featured, maxPrice, minRating]);

  useEffect(() => { load(); }, [load]);

  const set = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (!val || val === 'All' || val === '' || val === 0) p.delete(key);
    else p.set(key, String(val));
    setSearchParams(p);
  };

  const isFood = type === 'food';
  const isShopping = type === 'shopping';
  const cats = isFood ? FOOD_CATS : isShopping ? SHOP_CATS : ['All', ...FOOD_CATS.slice(1), ...SHOP_CATS.slice(1)];

  const getTitle = () => {
    if (search) return `"${search}"`;
    if (featured) return 'Featured Items';
    if (isFood) return 'Food Delivery';
    if (isShopping) return 'Luxury Shopping';
    return 'All Products';
  };

  return (
    <div style={{ paddingTop: 68 }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg-2)', borderBottom: '1px solid var(--border)',
        padding: 'clamp(28px,5vw,52px) clamp(14px,4vw,20px) clamp(20px,4vw,36px)',
      }}>
        <div className="container" style={{ padding: 0 }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-3)', marginBottom: 16, letterSpacing: '0.05em' }}>
            <Link to="/" style={{ color: 'var(--text-3)', transition: 'color 0.2s' }}
              onMouseEnter={e => e.target.style.color = 'var(--gold)'}
              onMouseLeave={e => e.target.style.color = 'var(--text-3)'}
            >Home</Link>
            <span>/</span>
            <span style={{ color: 'var(--text)' }}>Shop</span>
            {category !== 'All' && <><span>/</span><span style={{ color: 'var(--gold)' }}>{category}</span></>}
          </div>
          <p className="section-eyebrow">{isFood ? 'Food Delivery' : isShopping ? 'Luxury Shopping' : 'Everything'}</p>
          <h1 className="section-title">{getTitle()}</h1>
          <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 8 }}>
            {products.length} {products.length === 1 ? 'item' : 'items'} found
          </p>
        </div>
      </div>

      <div className="container" style={{ padding: 'clamp(20px,4vw,36px) clamp(14px,4vw,20px)' }}>
        {/* Type switcher */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', rowGap: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['', 'All'], ['food', '🍽 Food'], ['shopping', '✦ Shopping']].map(([val, label]) => (
              <button key={val} onClick={() => set('type', val)} style={{
                padding: '9px 20px', fontSize: 11, fontWeight: 500,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                background: (type === val || (!type && val === '')) ? (val === 'food' ? 'var(--emerald)' : 'var(--gold)') : 'transparent',
                color: (type === val || (!type && val === '')) ? (val === 'food' ? 'white' : '#09080A') : 'var(--text-2)',
                border: '1px solid',
                borderColor: (type === val || (!type && val === '')) ? 'transparent' : 'var(--border)',
                borderRadius: 3, cursor: 'pointer', transition: 'all 0.25s',
              }}>{label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <select
              value={sort}
              onChange={e => set('sort', e.target.value)}
              className="input"
              style={{ width: '100%', minWidth: 140, maxWidth: 220, padding: '9px 14px', fontSize: 12 }}
            >
              <option value="">Sort: Featured</option>
              <option value="price_asc">Price: Low → High</option>
              <option value="price_desc">Price: High → Low</option>
              <option value="rating">Best Rated</option>
              <option value="popular">Most Popular</option>
            </select>
            <button onClick={() => setFiltersOpen(!filtersOpen)}
              className="btn btn-outline btn-sm"
              style={{ gap: 8 }}
            >
              ⚙ Filters {filtersOpen ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* Category pills */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {cats.map(cat => (
            <button key={cat} onClick={() => set('category', cat)} style={{
              padding: '7px 16px', fontSize: 11, fontWeight: 400,
              letterSpacing: '0.08em', textTransform: 'uppercase',
              background: category === cat ? 'rgba(212,168,83,0.12)' : 'transparent',
              color: category === cat ? 'var(--gold)' : 'var(--text-3)',
              border: `1px solid ${category === cat ? 'var(--border-2)' : 'var(--border)'}`,
              borderRadius: 3, cursor: 'pointer', transition: 'all 0.25s',
            }}
            onMouseEnter={e => { if (category !== cat) { e.currentTarget.style.color = 'var(--text-2)'; e.currentTarget.style.borderColor = 'var(--border-2)'; }}}
            onMouseLeave={e => { if (category !== cat) { e.currentTarget.style.color = 'var(--text-3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}}
            >{cat}</button>
          ))}
        </div>

        {/* Filter panel */}
        {filtersOpen && (
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: 'clamp(16px,3vw,28px) clamp(14px,3vw,32px)', marginBottom: 24,
            animation: 'fade-up 0.25s ease',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 28 }}>
              <div>
                <label className="label">Max Price: ${maxPrice.toLocaleString()}</label>
                <input type="range" min={10} max={5000} step={50} value={maxPrice}
                  onChange={e => set('maxPrice', e.target.value)}
                  style={{ width: '100%', accentColor: 'var(--gold)', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
                  <span>$10</span><span>$5,000</span>
                </div>
              </div>
              <div>
                <label className="label">Min Rating</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  {[0,3,4,5].map(r => (
                    <button key={r} onClick={() => set('minRating', r)} style={{
                      padding: '7px 14px', fontSize: 11, borderRadius: 3, cursor: 'pointer',
                      background: minRating === r ? 'rgba(212,168,83,0.15)' : 'transparent',
                      border: `1px solid ${minRating === r ? 'var(--border-2)' : 'var(--border)'}`,
                      color: minRating === r ? 'var(--gold)' : 'var(--text-3)',
                      transition: 'all 0.2s',
                    }}>
                      {r === 0 ? 'All' : `${r}★+`}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label">Show</label>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <button onClick={() => set('featured', featured ? '' : 'true')} style={{
                    padding: '7px 14px', fontSize: 11, borderRadius: 3, cursor: 'pointer',
                    background: featured ? 'rgba(212,168,83,0.15)' : 'transparent',
                    border: `1px solid ${featured ? 'var(--border-2)' : 'var(--border)'}`,
                    color: featured ? 'var(--gold)' : 'var(--text-3)',
                    transition: 'all 0.2s',
                  }}>✦ Featured Only</button>
                </div>
              </div>
            </div>
            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setSearchParams({})} className="btn btn-ghost btn-sm">Clear All Filters</button>
            </div>
          </div>
        )}

        {/* Products */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'clamp(10px,2vw,20px)' }}>
            {[...Array(8)].map((_, i) => (
              <div key={i}>
                <div className="skeleton" style={{ paddingTop: '90%', borderRadius: 6, marginBottom: 12 }} />
                <div className="skeleton" style={{ height: 16, width: '70%', marginBottom: 8 }} />
                <div className="skeleton" style={{ height: 12, width: '45%' }} />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <div style={{ fontSize: 56, marginBottom: 20 }}>🔍</div>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 28, marginBottom: 12 }}>Nothing found</h3>
            <p style={{ color: 'var(--text-3)', marginBottom: 28 }}>Try different filters or browse all items.</p>
            <button onClick={() => setSearchParams({})} className="btn btn-gold">Clear Filters</button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 'clamp(10px,2vw,20px)' }}>
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} wishlistIds={userData?.wishlist || []} animDelay={Math.min(i, 7) * 0.06} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
